import { useEffect, useState } from 'react';

import PrimaryNav from '@skyscanner-internal/global-components/primary-nav';
import { trackEventFn } from '@skyscanner-internal/global-components/utils/tracking';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { translate } from '@web-platform/shared-universal-translations';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { getHeaderData } from '@web-platform/shared-universal-utils-frontend/src/singletons/globalHeaderData';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import observer from 'saddlebag-observer';

import type { PrimaryNavProps } from '@skyscanner-internal/global-components/primary-nav';

const flightsSearchControlsChangedEvent = 'flights-search-controls-changed';

type SearchContext = PrimaryNavProps['searchContext'];

type Props = {
  useCompactNavigation?: boolean;
};

export const PrimaryNavInFss = ({ useCompactNavigation = false }: Props = {}) => {
  const [searchContext, setSearchContext] = useState<SearchContext>();
  const headerData = getHeaderData();
  const isPackageExperimentEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.PACKAGES_HEADER_TAB_ENABLED,
  );
  const isStaysHeaderTabBubbleEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.HOTELS_HEADER_TAB_BUBBLE_ENABLED,
  );
  const packagesHeaderTabVersion = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.PACKAGES_HEADER_TAB_VERSION,
  );
  const staysStrings = getFeatureDecision(FEATURE_DECISION_KEYS.STAYS_COPY_HEADER_TAB_ENABLED);

  useEffect(() => {
    if (!headerData) {
      return undefined;
    }

    const subscription = observer.subscribe(
      flightsSearchControlsChangedEvent,
      (nextSearchContext: SearchContext) => {
        setSearchContext(nextSearchContext);
      },
    );

    return () => {
      observer.unsubscribe(subscription);
    };
  }, [headerData]);

  if (!headerData) {
    return null;
  }

  return (
    <PrimaryNav
      activeTab={headerData.activeTab}
      isOpeningNewTab={false}
      logger={{
        logOperationalEvent,
        logError,
        logWarn,
      }}
      searchContext={searchContext}
      showPackagesTab={isPackageExperimentEnabled}
      strings={{
        ...headerData.strings,
        carHire: translate('ktxtHeader_cars_label'),
        hotels: staysStrings ? translate('homepageTab_stays') : headerData.strings.hotels,
      }}
      trackEventFn={trackEventFn}
      unstable__hotelsNewBubble={Boolean(isStaysHeaderTabBubbleEnabled)}
      unstable__newBubble={
        ['NO_VISUAL_CHANGES_WITH_NEW_BUBBLE', 'VISUAL_CHANGE_AND_NEW_BUBBLE'].includes(
          packagesHeaderTabVersion,
        ) && isPackageExperimentEnabled
      }
      unstable__packagesEnableVisualChange={
        ['VISUAL_CHANGE_ONLY', 'VISUAL_CHANGE_AND_NEW_BUBBLE'].includes(packagesHeaderTabVersion) &&
        isPackageExperimentEnabled
      }
      urls={headerData.urls}
      useCompactNavigation={useCompactNavigation}
    />
  );
};
